import React, { useEffect, useState } from 'react';
import {
  Box,
  Collapse,
  Flex,
  IconButton,
  Stack,
  Text,
  useRadio,
  useRadioGroup,
} from '@podiumhq/design-system';

import { validBusinessInfoDetails } from 'components/BusinessDetails/utils';
import { useCheckoutContext } from 'contexts/CheckoutContextProvider';

import { BILLING_ADDRESS, BUSINESS_ADDRESS } from '../../constants';
import { paymentDetailsTitleStyles } from '../../styles';

import AddressContent from '../AddressContent';
import { ErrorNotification } from '../ErrorNotification';

import { BillingAddressForm } from './BillingAddressForm';

export const BillingAddressOptionsForm = () => {
  const { billingErrorMessage, businessAddress, setSelectedBillingAddress } =
    useCheckoutContext();

  const hasValidDetails = validBusinessInfoDetails(businessAddress);

  useEffect(() => {
    if (!hasValidDetails) {
      setSelectedBillingAddress(BILLING_ADDRESS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasValidDetails]);

  return (
    <>
      <Text {...paymentDetailsTitleStyles}>Billing Address</Text>

      {hasValidDetails ? (
        <BillingAddressOptionsRadioGroup />
      ) : (
        <>
          <ErrorNotification errorMessage={billingErrorMessage} />

          <NoBillingAddressCard />
        </>
      )}
    </>
  );
};

const BillingAddressOptionsRadioGroup = () => {
  const {
    businessAddress: businessDetails = {},
    selectedBillingAddress,
    setBillingAddressErrors,
    setSelectedBillingAddress,
  } = useCheckoutContext() || {};

  const updateSelectedBillingAddress = (value) => {
    if (value === BILLING_ADDRESS) {
      setBillingAddressErrors({});
    }

    setSelectedBillingAddress(value);
  };

  const { getRadioProps, getRootProps } = useRadioGroup({
    name: 'select-billing-address',
    defaultValue: selectedBillingAddress,
    onChange: updateSelectedBillingAddress,
  });

  const billingRadioProps = getRadioProps({
    id: BILLING_ADDRESS,
    key: BILLING_ADDRESS,
    value: BILLING_ADDRESS,
  });
  const businessRadioProps = getRadioProps({
    id: BUSINESS_ADDRESS,
    key: BUSINESS_ADDRESS,
    value: BUSINESS_ADDRESS,
  });
  const radioGroupProps = getRootProps();

  const cardContentWrapperStyles = { paddingLeft: '24px' };

  return (
    <Stack {...radioGroupProps}>
      <BillingAddressOptionsRadioCard
        label="Same as business address"
        {...businessRadioProps}
      >
        <Box {...cardContentWrapperStyles}>
          <AddressContent {...businessDetails} />
        </Box>
      </BillingAddressOptionsRadioCard>

      <BillingAddressOptionsRadioCard
        boxStyles={{
          alignItems: 'center',
          display: 'flex',
          height: '56px',
          mt: '16px',
        }}
        id="fs-different-billing-address-btn"
        label="Use a different billing address"
        {...billingRadioProps}
      >
        <BillingAddressForm key="billing-address" />
      </BillingAddressOptionsRadioCard>
    </Stack>
  );
};

const BillingAddressOptionsRadioCard = ({
  boxProps,
  children,
  label,
  boxStyles,
  ...props
}) => {
  const { getInputProps } = useRadio(props);

  const { style, ...inputProps } = getInputProps();

  return (
    <Box
      as="label"
      cursor={inputProps?.checked ? null : 'pointer'}
      {...cardStyles}
      {...boxProps}
    >
      <input {...inputProps} />

      <Text cursor="pointer" {...cardLabelStyles}>
        {label}
      </Text>

      <Collapse in={inputProps?.checked}>{children}</Collapse>
    </Box>
  );
};

const NoBillingAddressCard = () => {
  const { billingAddress, setBillingErrorMessage, setSelectedBillingAddress } =
    useCheckoutContext();
  const [displayForm, setDisplayForm] = useState(false);

  const shouldDisplayForm =
    displayForm || validBusinessInfoDetails(billingAddress);

  const openDisplayForm = () => {
    setBillingErrorMessage && setBillingErrorMessage('');
    setDisplayForm(true);
  };

  useEffect(() => {
    setSelectedBillingAddress && setSelectedBillingAddress(BILLING_ADDRESS);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      {...cardStyles}
      padding={shouldDisplayForm ? '24px' : '8px 6px 8px 20px'}
    >
      {!shouldDisplayForm && (
        <Flex
          align="center"
          id="add-billing-address"
          cursor="pointer"
          justify="space-between"
          onClick={openDisplayForm}
        >
          <Text {...labelStyles}>Add a billing address</Text>

          <IconButton
            fillColor="blue.base"
            size="md"
            sx={{
              background: 'none',
              border: 'none',
              outline: 'none',
              _hover: { background: 'none' },
            }}
            type="PlusCircleSolid"
            variant="ghost"
          />
        </Flex>
      )}

      <Collapse in={shouldDisplayForm}>
        <BillingAddressForm key="no-address-found" mt="0" />
      </Collapse>
    </Box>
  );
};

const cardLabelStyles = {
  display: 'inline',
  fontSize: 'md',
  fontWeight: 'medium',
  paddingLeft: '10px',
};

const cardStyles = {
  border: '1px solid #C8CFD9',
  borderRadius: '15px',
  padding: '24px',
};

const labelStyles = {
  display: 'inline',
  fontWeight: 'medium',
  color: 'blue.base',
  fontSize: 'lg',
  margin: 0,
};
